import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    // Verificar si hay sesión guardada al cargar la app
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        // Verificar si el token ya expiró (decodificar payload sin validar firma)
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);

        if (payload.exp && payload.exp < now) {
          // Token expirado — limpiar y marcar como sesión expirada
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setSessionExpired(true);
        } else {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error al parsear sesión guardada:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    
    setLoading(false);
  }, []);

  // Interceptor global de axios: captura 401 = sesión expirada
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isAuthenticated) {
          // Token expirado o inválido en una petición
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
          setIsAuthenticated(false);
          setSessionExpired(true);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [isAuthenticated]);

  const login = (userData, token) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    setSessionExpired(false);
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
  };

  // Multi-tenant: devuelve el empresaId del usuario logueado
  const empresaId = user?.empresaId || null;

  // Helper para generar claves de localStorage aisladas por empresa
  const getTenantKey = useCallback((key) => {
    if (!empresaId) return key;
    return `${key}_${empresaId}`;
  }, [empresaId]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, empresaId, getTenantKey, loading, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};
